<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>Check remaining contacts</template>
        <template v-slot:subheader>Run #{{ props.run.id }} · {{ props.run.team_name }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <dl class="m-0 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
                <dt class="font-bold">Carried-over resets</dt>
                <dd class="m-0 text-right tabular-nums" data-test="follow-up-resets">
                    {{ countLabel(plan.carriedResets) }}
                </dd>
                <dt class="font-bold">Contacts to query</dt>
                <dd class="m-0 text-right tabular-nums" data-test="follow-up-queries">
                    {{ countLabel(plan.toQuery) }}
                </dd>
                <dt class="pl-4 text-slate-500">Not yet queried</dt>
                <dd class="m-0 text-right tabular-nums text-slate-500" data-test="follow-up-not-yet-queried">
                    {{ countLabel(plan.notYetQueried) }}
                </dd>
                <dt class="pl-4 text-slate-500">Ended in error</dt>
                <dd class="m-0 text-right tabular-nums text-slate-500" data-test="follow-up-error">
                    {{ countLabel(plan.error) }}
                </dd>
                <dt class="pl-4 text-slate-500">Changed during the run</dt>
                <dd class="m-0 text-right tabular-nums text-slate-500" data-test="follow-up-changed">
                    {{ countLabel(plan.changed) }}
                </dd>
                <template v-if="plan.awaitingFollowUp > 0">
                    <dt class="pl-4 text-slate-500">Awaiting follow-up query</dt>
                    <dd class="m-0 text-right tabular-nums text-slate-500" data-test="follow-up-awaiting">
                        {{ countLabel(plan.awaitingFollowUp) }}
                    </dd>
                </template>
                <template v-if="plan.rechecked > 0">
                    <dt class="font-bold">Checked again first</dt>
                    <dd class="m-0 text-right tabular-nums" data-test="follow-up-rechecked">
                        {{ countLabel(plan.rechecked) }}
                    </dd>
                </template>
            </dl>

            <p class="m-0" data-test="follow-up-explanation">
                The follow-up run applies the resets run #{{ props.run.id }} decided but did not apply, without querying
                those contacts again; each is read again first, and one that has opted in or out since run #{{
                    props.run.id
                }}
                queried it is left alone, as is one whose subscriptions or opt-in date attribute have changed in any
                other way, for Check remaining contacts to check again. It queries the contacts run #{{ props.run.id }}
                never queried, that ended in error or that changed during the run, and sends any follow-up query it did
                not send. The contacts it did not check for another reason (no consent date, an invalid or non-US
                number, or no longer subscribed) are checked again and queried if they are now eligible.
            </p>

            <p class="m-0" data-test="follow-up-billing">
                Each query is billed to Voxie, and reassigned contacts cost a few more. Contacts with reassigned numbers
                are reset: their data is cleared and both their subscriptions are set to no preference. Contacts deleted
                or no longer subscribed since are dropped without a query.
            </p>

            <div class="flex flex-col gap-1">
                <vx-checkbox
                    v-model="requiresApproval"
                    data-test="review-resets"
                    :aria-describedby="reviewDescriptionId"
                >
                    Review resets before applying
                </vx-checkbox>
                <p :id="reviewDescriptionId" class="m-0 pl-6 text-slate-500" data-test="review-resets-description">
                    {{ requiresApproval ? REVIEW_ON_TEXT : REVIEW_OFF_TEXT }}
                </p>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-follow-up"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Start follow-up run
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxCheckbox, VxModal } from '@voxie/frontend-components';
import { isAxiosError } from 'axios';
import { computed, ref, useId, watch } from 'vue';
import {
    REVIEW_OFF_TEXT,
    REVIEW_ON_TEXT,
    countLabel,
    followUpPlan,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun } from '~/services/adminRndClient.types';
import { HTTP_CONFLICT } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ run: RndRun }>();
const emit = defineEmits<{ started: [run: RndRun]; conflict: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const reviewDescriptionId = useId();

const submitting = ref(false);
const requiresApproval = ref(false);

const plan = computed(() => followUpPlan(props.run.counts));

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const { data } = await adminRndClient.createFollowUpRun(props.run.id, {
            requires_approval: requiresApproval.value,
        });

        emit('started', data);
        visible.value = false;
    } catch (error) {
        toasted.error(catchRequestError(error), { sticky: true });

        if (isAxiosError(error) && HTTP_CONFLICT === error.response?.status) {
            emit('conflict');
            visible.value = false;
        }
    } finally {
        submitting.value = false;
    }
};

watch(visible, (open) => {
    if (open) {
        requiresApproval.value = false;
    }
});
</script>
